import {
  Grid,
  Paper,
  alpha,
  Typography,
  Box,
  Icon,
  Skeleton,
} from "@mui/material";
import React, { Fragment, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { theme } from "../../../../theme";
import { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { IBucket } from "../../../../app.interface";
import { posService } from "../../../../services/pos.service";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

interface TopSellingItem {
  name: string;
  quantity: number;
  orderCount: number;
}

export const POSDashboardMenuPerformance: React.FC<{
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}> = ({ dateRange }) => {
  const [loading, setLoading] = React.useState(true);
  const [topSellingItems, setTopSellingItems] = React.useState<
    TopSellingItem[]
  >([]);
  const [topCashiers, setTopCashiers] = React.useState<IBucket[]>([]);

  const EmptyStateView: React.FC<{
    title: string;
    message: string;
    icon: string;
    height?: number;
  }> = ({ title, message, icon, height = 350 }) => (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        p: 3,
        textAlign: "center",
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha("#1E8078", 0.1),
            color: "#1E8078",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Icon sx={{ fontSize: 40 }}>{icon}</Icon>
        </Box>
      </motion.div>

      <Typography
        variant="h6"
        component={motion.h6}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        component={motion.p}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        sx={{ maxWidth: 400 }}
      >
        {message}
      </Typography>
    </Box>
  );

  // Popular Items Chart Data
  const popularItemsOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "70%",
      },
    },
    colors: ["#1E8078"],
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: topSellingItems.map((item) => item.name),
      labels: {
        style: {
          colors: theme.palette.text.secondary,
        },
        formatter: function (val: any) {
          const value = String(val);
          return value.length > 20 ? value.substring(0, 20) + "..." : value;
        },
      },
      tooltip: {
        enabled: true,
        offsetY: -2,
        style: {
          fontSize: "12px",
          fontFamily: theme.typography.fontFamily,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.secondary,
        },
        maxWidth: 150,
      },
    },
    tooltip: {
      y: {
        title: {
          formatter: () => "Quantity",
        },
      },
      x: {
        formatter: function (val: any) {
          return String(val);
        },
      },
    },
    grid: {
      show: false,
    },
  };

  const popularItemsData = [
    {
      name: "Orders",
      data: topSellingItems.map((item) => item.quantity),
    },
  ];

  // Top Cashiers Chart Options
  const topCashiersOptions: ApexOptions = {
    chart: {
      type: "donut",
    },
    colors: ["#1E8078", "#26A69A", "#4DB6AC", "#80CBC4", "#B2DFDB"],
    labels: topCashiers.slice(0, 5).map((item) => {
      const name = item.key;
      return name.length > 15 ? name.substring(0, 15) + "..." : name;
    }),
    legend: {
      position: "bottom",
      formatter: function (label: string, opts) {
        return `${label}: ${opts.w.globals.series[opts.seriesIndex]}`;
      },
      labels: {
        colors: theme.palette.text.secondary,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "14px",
              color: theme.palette.text.secondary,
            },
            value: {
              show: true,
              fontSize: "16px",
              fontWeight: 600,
              color: theme.palette.text.primary,
              formatter: function (val: string) {
                return val;
              },
            },
            total: {
              show: true,
              label: "Total Orders",
              color: theme.palette.text.secondary,
              formatter: function (w) {
                return w.globals.seriesTotals
                  .reduce((a: number, b: number) => a + b, 0)
                  .toString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: function (value: number) {
          return `${value} orders`;
        },
      },
    },
  };

  const topCashiersData = [
    {
      name: "Orders",
      data: topCashiers.slice(0, 5).map((item) => item.doc_count),
    },
  ];

  const fetchMenuPerformanceData = () => {
    setLoading(true);
    posService
      .getDashboardMenuPerformanceData({
        start_date: (dateRange[0]?.unix() ?? 0) * 1000,
        end_date: (dateRange[1]?.unix() ?? 0) * 1000,
      })
      .then(
        (res: {
          topSellingItems: TopSellingItem[];
          topCashiers: IBucket[];
        }) => {
          setTopSellingItems(res.topSellingItems);
          setTopCashiers(res.topCashiers);
        }
      )
      .catch(() => {
        toast.error("Error fetching menu performance data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(fetchMenuPerformanceData, [dateRange]);

  const LoadingBarChartSkeleton = () => (
    <Skeleton
      variant="rectangular"
      width="100%"
      height={350}
      sx={{
        transform: "none",
        borderRadius: 1,
      }}
    />
  );

  return (
    <Fragment>
      <Grid
        container
        spacing={3}
        sx={{
          m: 0,
          width: "100%",
        }}
      >
        <Grid item xs={12} md={8} sx={{ p: "12px !important" }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Top Selling Items
            </Typography>
            {loading ? (
              <LoadingBarChartSkeleton />
            ) : topSellingItems.length === 0 ? (
              <EmptyStateView
                title="No Sales Data"
                message="There are no sales recorded for the selected period. Try selecting a different time range."
                icon="inventory"
              />
            ) : (
              <ReactApexChart
                options={popularItemsOptions}
                series={popularItemsData}
                type="bar"
                height={350}
              />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ p: "12px !important" }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Top Performing Cashiers
            </Typography>
            {loading ? (
              <LoadingBarChartSkeleton />
            ) : topCashiers.length === 0 ? (
              <EmptyStateView
                title="No Cashier Data"
                message="There is no cashier performance data available for the selected period."
                icon="people"
              />
            ) : (
              <ReactApexChart
                options={topCashiersOptions}
                series={topCashiers.slice(0, 5).map((item) => item.doc_count)}
                type="donut"
                height={350}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Fragment>
  );
};
